import { ComponentRef, inject, Injectable, Injector } from '@angular/core';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { SideOverlayRef } from './side-overlay-ref';
import { SIDE_OVERLAY_DATA, SideOverlayConfig } from './side-overlay.model';

@Injectable({
  providedIn: 'root',
})
export class SideOverlayService {
  private injector = inject(Injector);
  private overlay = inject(Overlay);


  open<T, D = any>(component: ComponentType<T>,
    config: SideOverlayConfig<D> = {},
  ): SideOverlayRef {
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };
    const overlayRef = this.createOverlay(dialogConfig);
    const sideOverlyRef = new SideOverlayRef(overlayRef);
    this.attachDialogContainer(component, overlayRef, dialogConfig, sideOverlyRef);
    overlayRef.backdropClick().subscribe(() => sideOverlyRef.close());

    return sideOverlyRef;
  }

  private createOverlay(config: SideOverlayConfig): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);

    return this.overlay.create(overlayConfig);
  }

  private attachDialogContainer(component: ComponentType<any>,
    overlayRef: OverlayRef,
    config: SideOverlayConfig, dialogRef: SideOverlayRef): ComponentType<any> {
    const injector = this.createInjector(config, dialogRef);
    const containerPortal = new ComponentPortal(component, null, injector);
    const containerRef: ComponentRef<any> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: SideOverlayConfig,
    dialogRef: SideOverlayRef): Injector {
    return Injector.create({
      providers: [
        { provide: SideOverlayRef, useValue: dialogRef },
        { provide: SIDE_OVERLAY_DATA, useValue: config.data },
      ],
    });
  }

  private getOverlayConfig(config: SideOverlayConfig): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .global()
      .left('0')
      .top('0');

    return new OverlayConfig({
      height: '100vh',
      width: window.innerWidth < 640 ? '90vw' : '50vw',
      disposeOnNavigation: true,
      panelClass: 'oo-side-overlay-panel',
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy,
    });
  }
}

const DEFAULT_CONFIG: SideOverlayConfig = {
  hasBackdrop: true,
  backdropClass: 'oo-side-overlay-backdrop',
  data: null,
};
